// ==================== SISTEMA DE ARMAZENAMENTO OFFLINE ====================
// IndexedDB para biometrias e pontos pendentes

const DB_NAME = 'jornyx_ponto_offline';
const DB_VERSION = 2;

// Stores
const STORES = {
  BIOMETRIAS: 'biometrias',
  PONTOS_PENDENTES: 'pontos_pendentes',
  CONFIG_DISPOSITIVO: 'config_dispositivo',
  SYNC_LOG: 'sync_log'
};

let dbInstance = null;

// Inicializar IndexedDB
export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Store de biometrias
      if (!db.objectStoreNames.contains(STORES.BIOMETRIAS)) {
        const biometriaStore = db.createObjectStore(STORES.BIOMETRIAS, { keyPath: 'id' });
        biometriaStore.createIndex('empresa_id', 'empresa_id', { unique: false });
        biometriaStore.createIndex('empregado_id', 'empregado_id', { unique: false });
        biometriaStore.createIndex('setor', 'setor', { unique: false });
        biometriaStore.createIndex('localidade', 'localidade', { unique: false });
      }

      // Store de pontos pendentes (não sincronizados)
      if (!db.objectStoreNames.contains(STORES.PONTOS_PENDENTES)) {
        const pontosStore = db.createObjectStore(STORES.PONTOS_PENDENTES, { keyPath: 'id', autoIncrement: true });
        pontosStore.createIndex('empresa_id', 'empresa_id', { unique: false });
        pontosStore.createIndex('sincronizado', 'sincronizado', { unique: false });
        pontosStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Config do dispositivo
      if (!db.objectStoreNames.contains(STORES.CONFIG_DISPOSITIVO)) {
        db.createObjectStore(STORES.CONFIG_DISPOSITIVO, { keyPath: 'id' });
      }

      // Log de sincronização
      if (!db.objectStoreNames.contains(STORES.SYNC_LOG)) {
        const syncStore = db.createObjectStore(STORES.SYNC_LOG, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('empresa_id', 'empresa_id', { unique: false });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

// ==================== BIOMETRIAS ====================

// Salvar biometrias de uma empresa (filtradas por setor/localidade)
export const salvarBiometriasOffline = async (empresaId, biometrias, filtro = {}) => {
  const db = await initDB();
  const tx = db.transaction(STORES.BIOMETRIAS, 'readwrite');
  const store = tx.objectStore(STORES.BIOMETRIAS);

  // Primeiro, limpar biometrias antigas desta empresa
  const index = store.index('empresa_id');
  const existentes = await new Promise((resolve, reject) => {
    const req = index.getAll(empresaId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  for (const bio of existentes) {
    store.delete(bio.id);
  }

  // Salvar novas biometrias
  for (const bio of biometrias) {
    const bioData = {
      id: `${empresaId}_${bio.empregado_id}`,
      empresa_id: empresaId,
      empregado_id: bio.empregado_id,
      nome: bio.nome,
      cpf: bio.cpf,
      matricula: bio.matricula,
      setor: bio.setor || 'geral',
      localidade: bio.localidade || 'matriz',
      embeddings: bio.embeddings || [],
      updated_at: new Date().toISOString()
    };
    store.put(bioData);
  }

  // Registrar sincronização
  await registrarSync(empresaId, 'download', biometrias.length, filtro);

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
};

// Buscar biometrias offline (para reconhecimento)
export const getBiometriasOffline = async (empresaId, filtro = {}) => {
  const db = await initDB();
  const tx = db.transaction(STORES.BIOMETRIAS, 'readonly');
  const store = tx.objectStore(STORES.BIOMETRIAS);
  const index = store.index('empresa_id');

  const biometrias = await new Promise((resolve, reject) => {
    const req = index.getAll(empresaId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  // Aplicar filtros se necessário
  let resultado = biometrias;
  
  if (filtro.setor) {
    resultado = resultado.filter(b => b.setor === filtro.setor);
  }
  if (filtro.localidade) {
    resultado = resultado.filter(b => b.localidade === filtro.localidade);
  }

  return resultado;
};

// Contar biometrias offline
export const contarBiometriasOffline = async (empresaId) => {
  const biometrias = await getBiometriasOffline(empresaId);
  return biometrias.length;
};

// ==================== PONTOS PENDENTES ====================

// Salvar ponto quando offline
export const salvarPontoOffline = async (pontoData) => {
  const db = await initDB();
  const tx = db.transaction(STORES.PONTOS_PENDENTES, 'readwrite');
  const store = tx.objectStore(STORES.PONTOS_PENDENTES);

  const config = await getConfigDispositivo(pontoData.empresa_id);
  const sequencial = (config?.ultimo_sequencial || 0) + 1;

  const ponto = {
    empresa_id: pontoData.empresa_id,
    empregado_id: pontoData.empregado_id,
    empregado_nome: pontoData.empregado_nome,
    timestamp: pontoData.timestamp || new Date().toISOString(),
    tipo: pontoData.tipo || 'entrada',
    embedding_usado: pontoData.embedding_usado,
    distancia: pontoData.distancia,
    dispositivo_id: pontoData.dispositivo_id,
    sequencial_local: `${pontoData.dispositivo_id}-${String(sequencial).padStart(6, '0')}`,
    sincronizado: false,
    criado_em: new Date().toISOString()
  };

  store.add(ponto);

  // Atualizar sequencial
  await atualizarConfigDispositivo(pontoData.empresa_id, { ultimo_sequencial: sequencial });

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(ponto);
    tx.onerror = () => reject(tx.error);
  });
};

// Buscar pontos pendentes de sincronização
export const getPontosPendentes = async (empresaId = null) => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORES.PONTOS_PENDENTES, 'readonly');
    const store = tx.objectStore(STORES.PONTOS_PENDENTES);

    // Buscar todos os pontos e filtrar manualmente
    const todos = await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });

    // Filtrar não sincronizados
    let pendentes = todos.filter(p => !p.sincronizado);

    if (empresaId) {
      pendentes = pendentes.filter(p => p.empresa_id === empresaId);
    }
    
    return pendentes;
  } catch (err) {
    console.error('Erro ao buscar pontos pendentes:', err);
    return [];
  }
};

// Marcar pontos como sincronizados
export const marcarPontosSincronizados = async (pontoIds) => {
  const db = await initDB();
  const tx = db.transaction(STORES.PONTOS_PENDENTES, 'readwrite');
  const store = tx.objectStore(STORES.PONTOS_PENDENTES);

  for (const id of pontoIds) {
    const req = store.get(id);
    req.onsuccess = () => {
      const ponto = req.result;
      if (ponto) {
        ponto.sincronizado = true;
        ponto.sincronizado_em = new Date().toISOString();
        store.put(ponto);
      }
    };
  }

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
};

// Limpar pontos já sincronizados (manter últimos 7 dias)
export const limparPontosAntigos = async () => {
  const db = await initDB();
  const tx = db.transaction(STORES.PONTOS_PENDENTES, 'readwrite');
  const store = tx.objectStore(STORES.PONTOS_PENDENTES);

  const seteDiasAtras = new Date();
  seteDiasAtras.setDate(seteDiasAtras.getDate() - 7);

  const todos = await new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  for (const ponto of todos) {
    if (ponto.sincronizado && new Date(ponto.criado_em) < seteDiasAtras) {
      store.delete(ponto.id);
    }
  }

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
};

// ==================== CONFIG DISPOSITIVO ====================

// Gerar ID único do dispositivo
const gerarDispositivoId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let id = 'TOTEM-';
  for (let i = 0; i < 6; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
};

// Obter ou criar config do dispositivo
export const getConfigDispositivo = async (empresaId) => {
  const db = await initDB();
  const tx = db.transaction(STORES.CONFIG_DISPOSITIVO, 'readonly');
  const store = tx.objectStore(STORES.CONFIG_DISPOSITIVO);

  return new Promise((resolve, reject) => {
    const req = store.get(empresaId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

// Atualizar config do dispositivo
export const atualizarConfigDispositivo = async (empresaId, updates) => {
  const db = await initDB();
  const tx = db.transaction(STORES.CONFIG_DISPOSITIVO, 'readwrite');
  const store = tx.objectStore(STORES.CONFIG_DISPOSITIVO);

  let config = await getConfigDispositivo(empresaId);
  
  if (!config) {
    config = {
      id: empresaId,
      dispositivo_id: gerarDispositivoId(),
      ultimo_sequencial: 0,
      ultima_sincronizacao: null,
      filtro_setor: null,
      filtro_localidade: null,
      criado_em: new Date().toISOString()
    };
  }

  const configAtualizada = { ...config, ...updates, atualizado_em: new Date().toISOString() };
  store.put(configAtualizada);

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(configAtualizada);
    tx.onerror = () => reject(tx.error);
  });
};

// ==================== SYNC LOG ====================

const registrarSync = async (empresaId, tipo, quantidade, filtro = {}) => {
  const db = await initDB();
  const tx = db.transaction(STORES.SYNC_LOG, 'readwrite');
  const store = tx.objectStore(STORES.SYNC_LOG);

  store.add({
    empresa_id: empresaId,
    tipo, // 'download', 'upload', 'push_update'
    quantidade,
    filtro,
    timestamp: new Date().toISOString()
  });

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
};

// ==================== SINCRONIZAÇÃO ====================

// Sincronizar biometrias do servidor para offline
export const sincronizarBiometrias = async (empresaId, token, filtro = {}) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  try {
    // Construir query params
    const params = new URLSearchParams();
    if (filtro.setor) params.append('setor', filtro.setor);
    if (filtro.localidade) params.append('localidade', filtro.localidade);

    const url = `${API_URL}/api/empresas-ponto/biometrias-offline/${empresaId}${params.toString() ? '?' + params.toString() : ''}`;
    
    const res = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!res.ok) {
      throw new Error('Erro ao buscar biometrias');
    }

    const data = await res.json();
    
    // Salvar no IndexedDB
    await salvarBiometriasOffline(empresaId, data.biometrias || [], filtro);
    
    // Atualizar config
    await atualizarConfigDispositivo(empresaId, {
      ultima_sincronizacao: new Date().toISOString(),
      filtro_setor: filtro.setor || null,
      filtro_localidade: filtro.localidade || null
    });

    return {
      success: true,
      quantidade: data.biometrias?.length || 0
    };
  } catch (error) {
    console.error('Erro na sincronização:', error);
    return { success: false, error: error.message };
  }
};

// Enviar pontos pendentes para o servidor
export const enviarPontosPendentes = async (empresaId, token) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  try {
    const pendentes = await getPontosPendentes(empresaId);
    
    if (pendentes.length === 0) {
      return { success: true, enviados: 0 };
    }

    const res = await fetch(`${API_URL}/api/empresas-ponto/sincronizar-pontos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        empresa_id: empresaId,
        pontos: pendentes.map(p => ({
          empregado_id: p.empregado_id,
          timestamp: p.timestamp,
          tipo: p.tipo,
          dispositivo_id: p.dispositivo_id,
          sequencial_local: p.sequencial_local,
          distancia: p.distancia
        }))
      })
    });

    if (!res.ok) {
      throw new Error('Erro ao enviar pontos');
    }

    const data = await res.json();
    
    // Marcar como sincronizados
    await marcarPontosSincronizados(pendentes.map(p => p.id));
    
    // Registrar sync
    await registrarSync(empresaId, 'upload', pendentes.length);

    return {
      success: true,
      enviados: pendentes.length,
      resultado: data
    };
  } catch (error) {
    console.error('Erro ao enviar pontos:', error);
    return { success: false, error: error.message };
  }
};

// Sincronização completa (download + upload)
export const sincronizacaoCompleta = async (empresaId, token, filtro = {}) => {
  // Primeiro envia pontos pendentes
  const uploadResult = await enviarPontosPendentes(empresaId, token);
  
  // Depois baixa biometrias atualizadas
  const downloadResult = await sincronizarBiometrias(empresaId, token, filtro);

  // Limpar pontos antigos
  await limparPontosAntigos();

  return {
    upload: uploadResult,
    download: downloadResult
  };
};

// ==================== RECONHECIMENTO OFFLINE ====================

// Calcular distância euclidiana entre embeddings
const calcularDistancia = (embedding1, embedding2) => {
  if (!embedding1 || !embedding2 || embedding1.length !== embedding2.length) {
    return Infinity;
  }
  
  let soma = 0;
  for (let i = 0; i < embedding1.length; i++) {
    soma += Math.pow(embedding1[i] - embedding2[i], 2);
  }
  return Math.sqrt(soma);
};

// Encontrar empregado por embedding (offline)
export const reconhecerFaceOffline = async (empresaId, embeddingDetectado, threshold = 0.6) => {
  const biometrias = await getBiometriasOffline(empresaId);
  
  let melhorMatch = null;
  let menorDistancia = Infinity;

  for (const bio of biometrias) {
    if (!bio.embeddings || bio.embeddings.length === 0) continue;

    // Comparar com todos os embeddings cadastrados
    for (const embeddingCadastrado of bio.embeddings) {
      const distancia = calcularDistancia(embeddingDetectado, embeddingCadastrado);
      
      if (distancia < menorDistancia) {
        menorDistancia = distancia;
        melhorMatch = bio;
      }
    }
  }

  if (melhorMatch && menorDistancia <= threshold) {
    return {
      encontrado: true,
      empregado: melhorMatch,
      distancia: menorDistancia,
      confianca: Math.max(0, Math.min(100, (1 - menorDistancia / threshold) * 100))
    };
  }

  return {
    encontrado: false,
    distancia: menorDistancia
  };
};

// Verificar se há biometrias offline disponíveis
export const temBiometriasOffline = async (empresaId) => {
  try {
    const quantidade = await contarBiometriasOffline(empresaId);
    return quantidade > 0;
  } catch (err) {
    console.error('Erro ao verificar biometrias offline:', err);
    return false;
  }
};

// Obter status offline
export const getStatusOffline = async (empresaId) => {
  try {
    const config = await getConfigDispositivo(empresaId);
    const biometrias = await contarBiometriasOffline(empresaId);
    const pendentes = await getPontosPendentes(empresaId);

    return {
      dispositivo_id: config?.dispositivo_id || null,
      biometrias_offline: biometrias || 0,
      pontos_pendentes: pendentes?.length || 0,
      ultima_sincronizacao: config?.ultima_sincronizacao || null,
      filtro_setor: config?.filtro_setor || null,
      filtro_localidade: config?.filtro_localidade || null
    };
  } catch (err) {
    console.error('Erro ao obter status offline:', err);
    return {
      dispositivo_id: null,
      biometrias_offline: 0,
      pontos_pendentes: 0,
      ultima_sincronizacao: null,
      filtro_setor: null,
      filtro_localidade: null
    };
  }
};

export default {
  initDB,
  salvarBiometriasOffline,
  getBiometriasOffline,
  contarBiometriasOffline,
  salvarPontoOffline,
  getPontosPendentes,
  marcarPontosSincronizados,
  getConfigDispositivo,
  atualizarConfigDispositivo,
  sincronizarBiometrias,
  enviarPontosPendentes,
  sincronizacaoCompleta,
  reconhecerFaceOffline,
  temBiometriasOffline,
  getStatusOffline
};
